import { createAPIPath } from '@/config'
import { container, TYPES } from '@/libs/di/inversify.config'
import router, { createLoginRedirectHref, isRequireAuthRoute } from '@/router'
// MEMO:
// react に依存しないように @apollo/client ではなく @apollo/client/core を使用する
// https://www.apollographql.com/docs/react/migrating/apollo-client-3-migration#using-apollo-client-without-react
import type { ApolloLink } from '@apollo/client/core'
import {
  ApolloClient,
  createHttpLink,
  from,
  InMemoryCache,
} from '@apollo/client/core'
import type { NetworkError } from '@apollo/client/errors'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import type { ServerError } from '@apollo/client/link/utils'
import { DefaultApolloClient } from '@vue/apollo-composable'
import type { Plugin } from 'vue'

import { trackErrorOperation } from '@/sentry'
import { relayStylePagination } from '@apollo/client/utilities'
import type { GraphQLFormattedError } from 'graphql'
import generateIntrospection from '../apollo.fragment.generated'

export const defaultClient = new ApolloClient({
  link: buildLink(),
  connectToDevTools: import.meta.env.MODE !== 'production',
  cache: new InMemoryCache({
    possibleTypes: generateIntrospection.possibleTypes,
    typePolicies: {
      Query: {
        fields: {
          // 絞り込み状態によってキャッシュを切り替えるため、追加のキャッシュキーを第一引数で指定する
          // 事象: https://400f-jp.slack.com/archives/C037A321U8K/p1655094591078789?thread_ts=1654851578.229439&cid=C037A321U8K
          // ドキュメント: https://www.apollographql.com/docs/react/pagination/key-args/#setting-keyargs
          chatRooms: relayStylePagination(['statuses']),
          colleagueChatRooms: relayStylePagination(),
          karten: relayStylePagination(),
          okaneKollegeUsers: relayStylePagination(),
        },
      },
      Viewer: {
        // viewer は 1クライアントにつき、対応する 1つのオブジェクトのみを扱う
        // よって、key による識別が不要なので keyFields を空にする
        // 参考： https://www.apollographql.com/docs/react/caching/cache-configuration/#customizing-cache-ids
        //
        // また、ログアウト時に apollo の store をクリアしているので、ユーザーの切り替えによって発生する key の識別は不要になっている
        // .clearStore() などで全文検索すると該当箇所が見つかるはず
        keyFields: [],
      },
      PlannerProfile: {
        // PlannerProfile は 1クライアントにつき、ログインしている Planner のもののみを扱う
        keyFields: [],
      },
      AutoPostKartePlannerCommentSetting: {
        // AutoPostKartePlannerCommentSetting は 1クライアントにつき、ログインしている Planner のもののみを扱う
        keyFields: [],
      },
      Contract: {
        // Contract は 1クライアントにつき、ログインしている Planner のもののみを扱う
        keyFields: [],
      },
    },
  }),
})

const plugin = ((): Plugin => {
  // eslint-disable-next-line fp/no-let
  let called = false

  return app => {
    if (called) {
      console.warn('ApolloPlugin has been already installed')
      return
    }
    called = true

    app.provide(DefaultApolloClient, defaultClient)
  }
})()

export default plugin

const isServerError = (error?: NetworkError): error is ServerError => {
  if (!error) {
    return false
  }

  return 'statusCode' in error
}

function buildLink(): ApolloLink {
  const httpLink = createHttpLink({
    uri: createAPIPath('federationGateway', '/gateway/planner/query'),
    credentials: 'include',
  })

  const authLink = setContext((_, { headers }) => {
    const { apiToken } = container.get(TYPES.APIConfig)

    const routeParams = router.currentRoute.value.params as Record<
      string,
      string
    >

    const entranceGUID = routeParams.entranceGUID as string | undefined
    return {
      headers: {
        ...headers,
        ...(apiToken && { 'X-API-TOKEN': apiToken.toString() }),
        ...(entranceGUID && {
          '400f-biz-entrance-guid': entranceGUID,
        }),
      },
    }
  })

  const errorLink = onError(
    ({ networkError, graphQLErrors, operation, response }) => {
      trackErrorOperation(
        operation.operationName,
        JSON.stringify(operation.variables),
        JSON.stringify(response),
        JSON.stringify(graphQLErrors)
      )

      if (isServerError(networkError) && networkError.statusCode === 422) {
        window.alert('エラーが発生しました。リロードしてください。')
        return
      }

      const currentRoute = router.currentRoute.value
      if (
        hasAuthenticatedError(networkError, graphQLErrors) &&
        isRequireAuthRoute(currentRoute)
      ) {
        location.href = createLoginRedirectHref(
          currentRoute.path,
          currentRoute.query
        )
      }
    }
  )
  return from([errorLink, authLink, httpLink])
}

export const hasAuthenticatedError = (
  networkError: NetworkError | undefined,
  graphQLErrors: readonly GraphQLFormattedError[] | undefined
): boolean => {
  if (isServerError(networkError) && networkError.statusCode === 401) {
    return true
  }

  if (!graphQLErrors) {
    return false
  }

  const codes = graphQLErrors.flatMap(e => {
    return e.extensions ? e.extensions.code : []
  })

  return codes.includes('UNAUTHENTICATED')
}
